/**
 * Domain Layer - TimeRangePolicy
 * 診療時間帯ポリシー
 */

import {
  type TimeRange,
  type TimeRangePattern,
  TIME_RANGE_PATTERNS,
  createTimeRange,
  toStartDate,
  toEndDate,
  timeToMinutes,
} from './TimeRange';

/**
 * 現在時刻から最も近い時間帯パターンを取得する
 * @param now 現在日時
 * @returns 該当する時間帯パターンの開始・終了時刻
 */
export const getNearestTimeRangePattern = (
  now: Date
): { startTime: string; endTime: string } => {
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTime = currentHours * 60 + currentMinutes;

  for (let i = 0; i < TIME_RANGE_PATTERNS.length; i++) {
    const pattern = TIME_RANGE_PATTERNS[i];
    const executionTimeInMinutes = timeToMinutes(pattern.executionTime);

    // 次の実行時刻を超えていない場合
    if (executionTimeInMinutes > currentTime) {
      if (i === 0) {
        // Pattern 1が次のパターンの場合 → Pattern 1を返す
        return {
          startTime: pattern.startTime,
          endTime: pattern.endTime,
        };
      } else {
        // それ以外は前のパターンを返す
        const prevPattern = TIME_RANGE_PATTERNS[i - 1];
        return {
          startTime: prevPattern.startTime,
          endTime: prevPattern.endTime,
        };
      }
    }
  }

  // 全ての実行時間が過ぎている場合（19:46以降）
  // 最後のパターン（22:00~22:45）を返す
  const lastPattern = TIME_RANGE_PATTERNS[TIME_RANGE_PATTERNS.length - 1];
  return {
    startTime: lastPattern.startTime,
    endTime: lastPattern.endTime,
  };
};

/**
 * 日付をYYYY-MM-DD形式の文字列に変換する
 * @param date 日付
 * @returns YYYY-MM-DD形式の文字列
 */
const formatDateForTimeRange = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * デフォルトの時間範囲を生成する
 * @param now 現在日時（省略時は現在時刻）
 * @returns TimeRange値オブジェクト
 */
export const getDefaultTimeRange = (now: Date = new Date()): TimeRange => {
  const pattern = getNearestTimeRangePattern(now);
  const dateStr = formatDateForTimeRange(now);

  return createTimeRange(
    dateStr,
    pattern.startTime,
    dateStr,
    pattern.endTime
  );
};

/**
 * 時間範囲が完全に設定されているかチェックする
 * @param timeRange TimeRange値オブジェクト
 * @returns 全てのフィールドが設定されている場合true
 */
export const isTimeRangeComplete = (timeRange: TimeRange): boolean => {
  return !!(
    timeRange.startDate &&
    timeRange.startTime &&
    timeRange.endDate &&
    timeRange.endTime
  );
};

/**
 * 指定された日時が時間範囲内かどうかを判定する
 * @param dateTime 判定対象の日時
 * @param timeRange 時間範囲
 * @returns 範囲内の場合true
 */
export const isWithinTimeRange = (
  dateTime: Date,
  timeRange: TimeRange
): boolean => {
  if (!isTimeRangeComplete(timeRange)) {
    return true; // 時間範囲が不完全な場合は常にtrue
  }

  const startDate = toStartDate(timeRange);
  const endDate = toEndDate(timeRange);

  if (!startDate || !endDate) {
    return true; // パースに失敗した場合は常にtrue
  }

  return dateTime >= startDate && dateTime <= endDate;
};

/**
 * 文字列形式の日時が時間範囲内かどうかを判定する
 * @param dateTimeString 日時文字列（YYYY-MM-DD HH:mm または YYYY/MM/DD HH:mm形式）
 * @param timeRange 時間範囲
 * @returns 範囲内の場合true、パース失敗時はfalse
 */
export const isDateTimeStringWithinTimeRange = (
  dateTimeString: string,
  timeRange: TimeRange
): boolean => {
  if (!dateTimeString) {
    return false;
  }

  // 日時文字列をパース
  const normalized = dateTimeString.replace(/\//g, '-').replace(' ', 'T');
  const dateTime = new Date(normalized);

  if (isNaN(dateTime.getTime())) {
    return false;
  }

  return isWithinTimeRange(dateTime, timeRange);
};

/**
 * 時間帯パターンのIDから時間範囲を取得する
 * @param patternId パターンID（1-8）
 * @returns TimeRangePattern（見つからない場合はundefined）
 */
export const getTimeRangePatternById = (
  patternId: number
): TimeRangePattern | undefined => {
  return TIME_RANGE_PATTERNS.find((p) => p.id === patternId);
};

/**
 * 全ての時間帯パターンを取得する
 * @returns TimeRangePatternの配列
 */
export const getAllTimeRangePatterns = (): readonly TimeRangePattern[] => {
  return TIME_RANGE_PATTERNS;
};

/**
 * 時間範囲の時間部分のみを更新する
 * @param timeRange 元のTimeRange
 * @param startTime 新しい開始時刻
 * @param endTime 新しい終了時刻
 * @returns 更新されたTimeRange
 */
export const updateTimeRangeTimes = (
  timeRange: TimeRange,
  startTime: string,
  endTime: string
): TimeRange => {
  return createTimeRange(
    timeRange.startDate,
    startTime,
    timeRange.endDate,
    endTime
  );
};

/**
 * 時間範囲の日付部分のみを更新する
 * @param timeRange 元のTimeRange
 * @param startDate 新しい開始日付
 * @param endDate 新しい終了日付
 * @returns 更新されたTimeRange
 */
export const updateTimeRangeDates = (
  timeRange: TimeRange,
  startDate: string,
  endDate: string
): TimeRange => {
  return createTimeRange(
    startDate,
    timeRange.startTime,
    endDate,
    timeRange.endTime
  );
};
